import React, { useEffect, useMemo, useState } from 'react';
import { Card, Box, Typography, Divider, Skeleton, Select, MenuItem } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getAssessmentTotal } from '../../../../../redux/features/poinTrackerSlice';

export default function AssessmentTotalCard({ fromDate, toDate, selectedTeam }) {
  const dispatch = useDispatch();
  const { loading, responseData } = useSelector((state) => state.pointTracker.assessmentTotal);
  const [limit, setLimit] = useState(10);

  const dispatchParams = useMemo(() => {
    return {
      fromDate,
      toDate,
      wingId: selectedTeam?.id === -1 ? null : selectedTeam?.id,
    };
  }, [fromDate, toDate, selectedTeam?.id]);

  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(getAssessmentTotal(dispatchParams));
    }
  }, [dispatch, dispatchParams]);

  const assessmentData = useMemo(() => {
    const data = responseData?.data || [];
    return data.map((item, index) => ({
      daily: item.dailyAssessment,
      taken: item.assessmentTaken,
      percentage: item.percentage,
      date: item.date,
    }));
  }, [responseData]);

  const displayedData = useMemo(() => {
    return [...assessmentData].reverse().slice(0, limit);
  }, [assessmentData, limit]);
  return (
    <Card sx={{ p: 3, borderRadius: 4, boxShadow: 3, width: '100%' }}>
      {/* Title */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 4, color: '#0f172a' }}>
        Assessment Total
      </Typography>
      {loading ? (
        <Box>
          {/* Header Skeleton */}
          <Box sx={{ display: 'flex', mb: 1 }}>
            <Skeleton variant="text" sx={{ flex: 1 }} />
            <Skeleton variant="text" sx={{ flex: 1 }} />
            <Skeleton variant="text" sx={{ flex: 1 }} />
          </Box>

          <Divider sx={{ mb: 1 }} />

          {/* Rows Skeleton */}
          {[...Array(6)].map((_, index) => (
            <Box key={index}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.8,
                }}
              >
                <Skeleton variant="text" sx={{ flex: 1 }} />
                <Skeleton variant="text" sx={{ flex: 1 }} />
                <Skeleton variant="text" sx={{ flex: 1 }} />
              </Box>

              {index !== 5 && <Divider sx={{ opacity: 0.6 }} />}
            </Box>
          ))}
        </Box>
      ) : (
        <>
          {/* Header Row */}
          <Box sx={{ display: 'flex', textAlign: 'center', mb: 1 }}>
            <Typography
              variant="caption"
              sx={{ flex: 1, fontWeight: 'bold', color: 'text.secondary', lineHeight: 1.2 }}
            >
              Date
            </Typography>
            <Typography
              variant="caption"
              sx={{ flex: 1, fontWeight: 'bold', color: 'text.secondary', lineHeight: 1.2 }}
            >
              Daily <br /> Assessment
            </Typography>
            <Typography
              variant="caption"
              sx={{ flex: 1, fontWeight: 'bold', color: 'text.secondary', lineHeight: 1.2 }}
            >
              Assessment <br /> Taken
            </Typography>
            <Typography
              variant="caption"
              sx={{ flex: 1, fontWeight: 'bold', color: 'text.secondary', lineHeight: 1.2 }}
            >
              Percentage
            </Typography>
          </Box>

          <Divider sx={{ mb: 1 }} />

          {/* Data Rows */}
          {displayedData.map((row, index) => (
            <Box key={index}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.8, // Adjust vertical spacing
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" sx={{ flex: 1, color: '#64748b', fontWeight: 500 }}>
                  {row.date}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, color: '#64748b', fontWeight: 500 }}>
                  {row.daily}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, color: '#2563eb', fontWeight: 'bold' }}>
                  {row.taken}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, color: '#10b981', fontWeight: 'bold' }}>
                  {row.percentage}
                </Typography>
              </Box>

              {/* Don't show divider after the last item */}
              {index !== displayedData.length - 1 && <Divider sx={{ opacity: 0.6 }} />}
            </Box>
          ))}
        </>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <Select
          size="small"
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          sx={{
            height: 32,
            fontSize: 12,
            '& .MuiSelect-select': { py: 0.5, px: 1 },
          }}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </Select>

        <Typography variant="caption" fontSize={12}>
          {`${limit} / ${assessmentData.length}`}
        </Typography>
      </Box>
    </Card>
  );
}
